import React from 'react';
import { getCustomPropertyDisplayData } from '../TicketDetailInfo/TicketDetailInfo';
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import { ICustomProperty } from '../../interfaces/customproperties.interface';
import { PillList } from './PillList';
import { EMPTY_CELL } from './emptyCell';

/** A stored custom property answer as text: a list's ids ("12,14"), a number, true/false, or a string. */
export const answerText = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(',');
  return JSON.stringify(value) ?? '';
};

/** A multi-select list's picks by name, in the order stored - as the ticket page lists them. */
const pickedNames = (answers: Record<string, unknown>, property: ICustomProperty): string[] => {
  const ids = answerText(answers[property.PropertyId]).split(',').map(id => id.trim()).filter(Boolean);
  return (property.CustomPropertyListItems ?? [])
    .filter(item => ids.includes(String(item.Id)))
    .sort((a, b) => ids.indexOf(String(a.Id)) - ids.indexOf(String(b.Id)))
    .map(item => item.Value);
};

/**
 * A True/False property never answered on this ticket - one made before the property existed, or by email or Teams.
 * The ticket page shows it unticked, so the lists show it as a saved false is shown, "False", not the dash (Shaun,
 * 2026-10-20). Sorting and filtering still read the stored value, where it has none.
 */
const unsetBoolean = (answers: Record<string, unknown>, property: ICustomProperty): boolean =>
  CustomPropertyDataType_LABELS[property.DataType] === CustomPropertyDataType_LABELS.BOOL && answerText(answers[property.PropertyId]) === '';

/**
 * A custom property's cell at rest, in every list: a multi-select list's picks as pills, titled with the property's
 * name, and every other type as text, as before. An empty one shows the lists' dash.
 */
export const CustomPropertyResting = ({ answers, property }: { answers: Record<string, unknown>; property: ICustomProperty }) =>
  CustomPropertyDataType_LABELS[property.DataType] === CustomPropertyDataType_LABELS.LIST && property.CustomPropertyListConfiguration?.AllowMultiple ? (
    <PillList names={pickedNames(answers, property)} title={property.Name} />
  ) : (
    <div className="w-full mr-4" dir="rtl">
      <bdi>{getCustomPropertyDisplayData(answers, property) || (unsetBoolean(answers, property) ? 'False' : EMPTY_CELL)}</bdi>
    </div>
  );
